/**
 * Alerts Query Hooks (v2)
 *
 * Wraps /api/v2/alerts. List query polls for the AlarmProtocol page; the
 * three state-change mutations (acknowledge/resolve/reopen) optimistically
 * patch the list cache so click feedback is instant.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { alertsService } from 'src/api/services';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';
import type { Alert, AlertStatus, AlertType } from 'src/types';

export interface AlertsQueryFilters {
  installation_id?: string | null;
  type?: AlertType[];
  status?: AlertStatus[];
}

/**
 * List alerts. Filters are applied server-side. Polls every
 * VITE_POLLING_INTERVAL ms (default 5 min) so newly opened alerts appear
 * without a manual refresh.
 */
export function useAlertsQuery(filters: AlertsQueryFilters = {}, pollingEnabled: boolean = true) {
  const pollingInterval = Number(import.meta.env.VITE_POLLING_INTERVAL) || 300000;

  return useQuery<Alert[]>({
    queryKey: queryKeys.alerts.list(filters as Record<string, unknown>),
    queryFn: () =>
      alertsService.list({
        installation_id: filters.installation_id || undefined,
        type: filters.type,
        status: filters.status,
      }),
    staleTime: 2 * 60 * 1000,
    refetchInterval: pollingEnabled ? pollingInterval : false,
    refetchIntervalInBackground: true,
  });
}

// ---------------------------------------------------------------------------
// Manual create (admin-only on the backend)
// ---------------------------------------------------------------------------

export interface CreateAlertInput {
  installation_id: string;
  type: AlertType;
  timestamp: string;
  focus_point?: { x: number; y: number };
  severity?: string;
  message?: string;
  measurements?: Array<Record<string, unknown>>;
}

export function useCreateAlertMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<Alert, Error, CreateAlertInput>({
    mutationFn: (input) => alertsService.create(input) as Promise<Alert>,
    onSuccess: (created) => {
      const matches = queryClient.getQueriesData<Alert[]>({
        queryKey: queryKeys.alerts.lists(),
      });
      for (const [key, data] of matches) {
        if (!data) continue;
        queryClient.setQueryData<Alert[]>(key, [created, ...data]);
      }
    },
    onError: (err) => {
      toast.error('Failed to create alert');
      if (import.meta.env.DEV) console.error('Create alert error:', err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

// ---------------------------------------------------------------------------
// State-change mutations (optimistic update + rollback)
// ---------------------------------------------------------------------------

type Patch = Partial<Alert>;

/** Optimistically apply `patch` to whichever cached alert lists contain `id`. */
function patchAlertInLists(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  patch: Patch
): { previous: Map<readonly unknown[], Alert[]> } {
  const previous = new Map<readonly unknown[], Alert[]>();
  const matches = queryClient.getQueriesData<Alert[]>({ queryKey: queryKeys.alerts.lists() });
  for (const [key, data] of matches) {
    if (!data) continue;
    previous.set(key, data);
    queryClient.setQueryData<Alert[]>(
      key,
      data.map((a) => (a.id === id ? { ...a, ...patch } : a))
    );
  }
  return { previous };
}

function rollback(
  queryClient: ReturnType<typeof useQueryClient>,
  previous: Map<readonly unknown[], Alert[]>
): void {
  for (const [key, data] of previous) {
    queryClient.setQueryData(key, data);
  }
}

export function useAcknowledgeAlertMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => alertsService.acknowledge(id),
    onMutate: (id: string) =>
      patchAlertInLists(queryClient, id, {
        acknowledged: true,
        acknowledged_at: new Date().toISOString(),
      }),
    onError: (err, _id, ctx) => {
      if (ctx) rollback(queryClient, ctx.previous);
      toast.error('Failed to acknowledge alert');
      if (import.meta.env.DEV) console.error('Acknowledge alert error:', err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

export function useResolveAlertMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => alertsService.resolve(id),
    onMutate: (id: string) =>
      patchAlertInLists(queryClient, id, {
        resolved_at: new Date().toISOString(),
      }),
    onError: (err, _id, ctx) => {
      if (ctx) rollback(queryClient, ctx.previous);
      toast.error('Failed to resolve alert');
      if (import.meta.env.DEV) console.error('Resolve alert error:', err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

export function useReopenAlertMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => alertsService.reopen(id),
    onMutate: (id: string) =>
      patchAlertInLists(queryClient, id, { resolved_at: null, resolved_by_id: null }),
    onError: (err, _id, ctx) => {
      if (ctx) rollback(queryClient, ctx.previous);
      toast.error('Failed to reopen alert');
      if (import.meta.env.DEV) console.error('Reopen alert error:', err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

/** Optimistically remove an alert from every cached list. */
function removeAlertFromLists(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string
): { previous: Map<readonly unknown[], Alert[]> } {
  const previous = new Map<readonly unknown[], Alert[]>();
  const matches = queryClient.getQueriesData<Alert[]>({ queryKey: queryKeys.alerts.lists() });
  for (const [key, data] of matches) {
    if (!data) continue;
    previous.set(key, data);
    queryClient.setQueryData<Alert[]>(
      key,
      data.filter((a) => a.id !== id)
    );
  }
  return { previous };
}

export function useDeleteAlertMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => alertsService.delete(id),
    onMutate: (id: string) => removeAlertFromLists(queryClient, id),
    onError: (err, _id, ctx) => {
      if (ctx) rollback(queryClient, ctx.previous);
      toast.error('Failed to delete alert');
      if (import.meta.env.DEV) console.error('Delete alert error:', err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}
